import type {
	ConceptDataobject,
	ConceptAttribute,
	ConceptFunction,
	ConceptEvent,
	ConceptFragment,
} from "@lr-notitia/notitia-engineer/schemas";
import type { Graph, Node, Link, Anchor, Spawn } from "@livereader/graphly-d3";
import { AnchorType, LinkStrength } from "@livereader/graphly-d3";
import type {
	DataobjectSchema,
	CellBodySchema,
	CellSatelliteSchema,
	EventSchema,
} from "../../../store/graphly-templates";

import { useGraphStore } from "../../../store/graph";
import { useGraphlyTemplatesStore } from "../../../store/graphly-templates";
import { useExpertKnowledgeStore } from "../../../store/expert-knowledge";
import { useSymbolsStore } from "../../../store/symbols";

let graphStore: ReturnType<typeof useGraphStore>;
let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let expertKnowledgeStore: ReturnType<typeof useExpertKnowledgeStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	expertKnowledgeStore = useExpertKnowledgeStore();
	symbolsStore = useSymbolsStore();
}

export function graph(fragmentId: string): Graph {
	if (!graphStore || !graphlyTemplatesStore) init();
	const fragment = graphStore.concept.fragment.byId(fragmentId);
	if (!fragment) return { nodes: [], links: [] };
	const nodes: Node[] = [];
	const links: Link[] = [];

	const dataobjects = dataobjectsInFragment(fragment.id);
	const functions = functionsInFragment(fragment.id);
	const events = eventsInFragment(fragment.id);

	dataobjects.forEach((dataobject, i) => {
		nodes.push(dataobjectNode(dataobject, dataobjects.length, i, "leading"));
		nodes.push(dataobjectNode(dataobject, dataobjects.length, i, "trailing"));
	});
	functions.forEach((func) => {
		nodes.push(functionNode(func, fragment));
		const inputsCount = func.inputDataobjects.length + func.inputEmitters.length;
		const inputAngles = satelliteAngles(inputsCount, 270).reverse();
		const inputs = [...func.inputDataobjects, ...func.inputEmitters];
		for (let i = 0; i < inputs.length; i++) nodes.push(inputSatellite(func, inputs[i], inputAngles[i]));
		const emitterAngles = satelliteAngles(func.emitters.length, 90);
		func.emitters.forEach((emitter, i) => {
			nodes.push(emitterSatellite(func, emitter, emitterAngles[i]));
		});
	});
	events.forEach((event) => {
		nodes.push(eventNode(event, fragment));
	});

	functions.forEach((func) => {
		func.inputDataobjects.forEach((input) => {
			links.push({
				source: input + "_input",
				target: func.id + "_" + input + "_input",
				strength: LinkStrength.Loose,
				curvature: 0.05,
				directed: true,
			});
		});
		func.emitters.forEach((emitterId) => {
			const emitter = graphStore.concept.emitter.byId(emitterId);
			if (!emitter) return;
			const recievingFunctions = functions.filter((f) => f.inputEmitters.includes(emitterId));
			recievingFunctions.forEach((recFunc) => {
				links.push({
					source: emitter.id,
					target: recFunc.id + "_" + emitter.id + "_input",
					strength: LinkStrength.Loose,
					curvature: 0.05,
					directed: true,
					label: symbolsStore.term({ id: emitter.refersTo, terminology: emitter.terminology || {} }),
				});
			});
			emitter.propagatesTo.forEach((attribute) => {
				const dataobject = dataobjects.find((d) => composedAttributes(d.id).includes(attribute))?.id;
				links.push({
					source: emitter.id,
					target: dataobject + "_output",
					strength: LinkStrength.Loose,
					curvature: 0.05,
					directed: true,
					label: symbolsStore.term({ id: emitter.refersTo, terminology: emitter.terminology || {} }),
				});
			});
			emitter.activates.forEach((event) => {
				links.push({
					source: emitter.id,
					target: event,
					strength: LinkStrength.Loose,
					curvature: 0.05,
					directed: true,
					label: symbolsStore.term({ id: emitter.refersTo, terminology: emitter.terminology || {} }),
				});
			});
		});
	});

	return { nodes, links };
}

export function children(dataobjectId: string, depth = 0): string[] {
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	const dataobjects = [...dataobject.composedBy];
	if (depth != 0) {
		dataobjects.forEach((d) => {
			dataobjects.push(...children(d, depth - 1).filter((c) => !dataobjects.includes(c)));
		});
	}
	return dataobjects;
}

export type ComposedDataobject = {
	id: string;
	dataobjects: ComposedDataobject[];
	attributes: string[];
};
export function composeDataobject(dataobjectId: string): ComposedDataobject {
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return { id: "", dataobjects: [], attributes: [] };
	return {
		id: dataobject.id,
		attributes: [...dataobject.consistsOf],
		dataobjects: dataobject.composedBy.map((d) => composeDataobject(d)),
	};
}

export function composedAttributes(dataobjectId: string): string[] {
	if (!graphStore) init();
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	return [
		...dataobject.consistsOf,
		...(children(dataobject.id, -1)
			.map((c) => graphStore.concept.dataobject.byId(c)?.consistsOf)
			.flat()
			.filter((c) => c) as string[]),
	];
}

function dataobjectsInFragment(fragmentId: string): ConceptDataobject[] {
	const fragment = graphStore.concept.fragment.byId(fragmentId);
	if (!fragment) return [];
	const dataobjects = [...fragment.dataobjects];
	return dataobjects
		.map((d) => graphStore.concept.dataobject.byId(d))
		.filter((d) => d !== undefined) as ConceptDataobject[];
}
function functionsInFragment(fragmentId: string): ConceptFunction[] {
	const fragment = graphStore.concept.fragment.byId(fragmentId);
	if (!fragment) return [];
	const functions = [...fragment.functions];
	return functions
		.map((f) => graphStore.concept.function.byId(f))
		.filter((f) => f !== undefined) as ConceptFunction[];
}
function eventsInFragment(fragmentId: string): ConceptEvent[] {
	const fragment = graphStore.concept.fragment.byId(fragmentId);
	if (!fragment) return [];
	const events = [...fragment.events];
	return events.map((e) => graphStore.concept.event.byId(e)).filter((e) => e !== undefined) as ConceptEvent[];
}

function dataobjectNode(
	dataobject: ConceptDataobject,
	count: number,
	index: number,
	position: "leading" | "trailing"
): Node<DataobjectSchema> {
	const pos = dataobjectPositions(count, position)[index];
	const tags = (
		composedAttributes(dataobject.id)
			.map((a) => graphStore.concept.attribute.byId(a))
			.filter((a) => a !== undefined) as ConceptAttribute[]
	)
		.sort((a, b) => a.order - b.order)
		.map((a) => symbolsStore.term({ id: a.refersTo, terminology: a.terminology || {} }));
	return {
		id: dataobject.id + (position == "leading" ? "_input" : "_output"),
		shape: {
			type: "dataobject",
			scale: 1,
			url: graphlyTemplatesStore.dataobjectShape,
		},
		x: pos.x,
		y: pos.y,
		anchor: {
			type: AnchorType.Hard,
			x: pos.x,
			y: pos.y,
		},
		payload: {
			title: symbolsStore.term({ id: dataobject.refersTo, terminology: dataobject.terminology || {} }),
			stacked: false,
			emphasized: false,
			tags: tags,
			type: "collection",
			color: dataobject.color,
		},
	};
}
function functionNode(func: ConceptFunction, fragment: ConceptFragment): Node<CellBodySchema> {
	return {
		id: func.id,
		shape: {
			type: "cell-body",
			scale: 1,
			url: graphlyTemplatesStore.cellBodyShape,
		},
		...position(func, fragment),
		payload: {
			name: symbolsStore.term({ id: func.refersTo, terminology: func.terminology || {} }),
		},
	};
}
function eventNode(event: ConceptEvent, fragment: ConceptFragment): Node<EventSchema> {
	const term = symbolsStore.term({ id: event.refersTo, terminology: event.terminology || {} });
	return {
		id: event.id,
		shape: {
			type: "event",
			scale: 1,
			url: graphlyTemplatesStore.eventShape,
		},
		...position(event, fragment),
		payload: {
			mode: "interrupt",
			type: "notitia",
			color: "",
			alignment: "down",
			note: term,
		},
	};
}
function inputSatellite(func: ConceptFunction, id: string, angle: number): Node<CellSatelliteSchema> {
	return {
		id: func.id + "_" + id + "_input",
		shape: {
			type: "cell-satellite",
			scale: 0.75,
			url: graphlyTemplatesStore.cellSatelliteShape,
		},
		gravity: 0,
		satellite: {
			source: func.id,
			angle: angle,
			distance: 110,
			type: "hard",
		},
		payload: {
			type: "input",
		},
	};
}
function emitterSatellite(func: ConceptFunction, emitterId: string, angle: number): Node<CellSatelliteSchema> {
	return {
		id: emitterId,
		shape: {
			type: "cell-satellite",
			scale: 0.75,
			url: graphlyTemplatesStore.cellSatelliteShape,
		},
		gravity: 0,
		satellite: {
			source: func.id,
			angle: angle,
			distance: 110,
			type: "hard",
		},
		payload: {
			type: "output",
		},
	};
}

function dataobjectPositions(count: number, position: "leading" | "trailing") {
	const x = 1500 * (position == "leading" ? -1 : 1);
	const step = 400;
	let y = -(step * (count - 1)) / 2;
	const positions = [];
	for (let i = 0; i < count; i++) {
		positions.push({ x: x, y: y });
		y += step;
	}
	return positions;
}
function satelliteAngles(count: number, angle: number, range = 45) {
	if (count == 1) return [angle];
	const step = range / (count - 1);
	const angles = [];
	for (let i = 0; i < count; i++) {
		angles.push(angle + (i * step - range / 2));
	}
	return angles;
}
function position(
	node: ConceptFunction | ConceptEvent,
	fragment: ConceptFragment
): { x: number | undefined; y: number | undefined; anchor: Anchor | undefined; spawn: Spawn | undefined } {
	if (!expertKnowledgeStore) init();
	const pos = expertKnowledgeStore.nodePositions[fragment.id]?.[node.id];
	if (pos) {
		return {
			x: pos.x,
			y: pos.y,
			anchor: { type: AnchorType.Soft, x: pos.x, y: pos.y },
			spawn: undefined,
		};
	} else {
		return {
			x: undefined,
			y: undefined,
			anchor: undefined,
			spawn: undefined,
		};
	}
}
